import {
  Alert,
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { Employee, Laboratory } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { SyntheticEvent, useEffect, useState } from "react"

type AddLaboratoriesDialogProps = {
  open: boolean
  onClose: () => void
  formState: Laboratory
  onSubmit: (arg: Laboratory) => void
}

type ValidationErrors = {
  name?: string
}

export const AddLaboratoriesDialog = (props: AddLaboratoriesDialogProps) => {
  const { open, onClose, formState, onSubmit } = props
  const [localFormState, setLocalFormState] = useState<Laboratory>(formState)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})

  const { employees } = useStoreState((state) => state)
  const { fetchEmployees } = useStoreActions((actions) => actions)

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        await fetchEmployees({ fresh: false })
      } catch (err) {
        console.error("Error loading employees:", err)
      }
    }
    loadEmployees()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setLocalFormState(formState)
    setValidationErrors({}) // Clear errors when form state changes
  }, [formState])

  const validateForm = (): ValidationErrors => {
    const errors: ValidationErrors = {}

    // Check required fields
    if (!localFormState.name?.trim()) {
      errors.name = "Bezeichnung ist erforderlich"
    }

    return errors
  }

  const isFormValid = (): boolean => {
    const errors = validateForm()
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleFormChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target
    if (name) {
      setLocalFormState((prevState) => ({ ...prevState, [name]: value }))

      // Clear validation error for this field
      if (validationErrors[name as keyof ValidationErrors]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors[name as keyof ValidationErrors]
          return newErrors
        })
      }
    }
  }

  const handleFormChangeEmployee = (_: SyntheticEvent<Element, Event>, value: Employee | null) => {
    if (value) {
      setLocalFormState((prevState) => ({
        ...prevState,
        manager: value.id,
      }))
    }
  }

  const handleSave = () => {
    if (isFormValid()) {
      onSubmit(localFormState)
    }
  }

  const handleClose = () => {
    setLocalFormState(formState)
    setValidationErrors({})
    onClose()
  }

  const hasValidationErrors = Object.keys(validationErrors).length > 0

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>
        {localFormState.id !== "NEW" ? "Laborbereich bearbeiten" : "Laborbereich hinzufügen"}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {hasValidationErrors && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
              Bitte korrigieren Sie die folgenden Fehler:
            </Typography>
            <Stack spacing={0.5}>
              {Object.entries(validationErrors).map(([field, error]) => (
                <Typography key={field} variant="body2">
                  • {error}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        <TextField
          size="small"
          name="name"
          label="Bezeichnung"
          type="text"
          fullWidth
          value={localFormState.name}
          onChange={handleFormChange}
          sx={{ mt: 1 }}
          required
          error={!!validationErrors.name}
          helperText={validationErrors.name}
        />

        <Autocomplete
          size="small"
          options={employees}
          getOptionLabel={(option) => option.EmployeeName || ""}
          renderInput={(params) => <TextField {...params} label="Laborleiter" />}
          value={employees.find((user) => user.id === localFormState.manager)}
          onChange={handleFormChangeEmployee}
        />
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" onClick={handleClose} variant="contained" />
        <StyledButton
          buttonType="save"
          onClick={handleSave}
          disabled={hasValidationErrors}
          variant="contained"
        />
      </DialogActions>
    </Dialog>
  )
}
